import { useCallback, useEffect, useRef, useState } from 'react';
import { IonAlert, IonContent, IonFooter, IonIcon, IonPage, IonToolbar } from '@ionic/react';
import { copyOutline, createOutline, shareSocialOutline } from 'ionicons/icons';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { DishInfoCard } from '../components/listings/DishInfoCard';
import { ListingLocationMap } from '../components/listings/ListingLocationMap';
import {
  PortionPickerModal,
  type PortionPickerResult,
} from '../components/listings/PortionPickerModal';
import { PublishedSharePromptModal } from '../components/listings/PublishedSharePromptModal';
import { TopBar } from '../components/ui/TopBar';
import { PullToRefresh } from '../components/ui/PullToRefresh';
import { useListingDetail, useCreateShareLink, usePatchListing } from '../hooks/useListings';
import { OrderCard } from '../components/orders/OrderCard';
import { OrderDetailCard } from '../components/orders/OrderDetailCard';
import { DropoffRunPanel } from '../components/listings/DropoffRunPanel';
import { CancelListingModal } from '../components/listings/CancelListingModal';
import { useReserveOrder, useConfirmOrder, useListingOrders, useMarkAllOrdersReady } from '../hooks/useOrders';
import { formatPriceCents } from '../utils/currency';
import { getListingAvailability } from '../utils/listingAvailability';
import { useAuthStore } from '../store/useAuthStore';
import { useDisplayProfile } from '../hooks/useDisplayProfile';
import { SignInModal } from '../components/auth/SignInModal';
import { AddressCaptureModal } from '../components/location/AddressCaptureModal';
import { DistanceLocationGateModal } from '../components/location/DistanceLocationGateModal';
import { useLocationStore } from '../store/useLocationStore';
import { useProfileStore } from '../store/useProfileStore';
import { hasOrderableProfileAddress } from '../types/profile';
import { useLocale } from '../hooks/useLocale';
import { showErrorModal } from '../services/errorModalService';
import { fireConfetti } from '../utils/confetti';
import { consumeJustPublished } from '../utils/justPublished';
import { markListingStale, consumeListingStale } from '../utils/staleListing';
import { isRelistSource } from '../utils/listingRelist';
import { logShareFailure } from '../utils/shareLog';
import type { OrderStatus } from '../types/order';

// Orders in these states are done and no longer "active" — canceling the
// listing doesn't affect them, so they shouldn't drive the cancel warning.
const TERMINAL_ORDER_STATUSES: OrderStatus[] = [
  'completed',
  'canceled',
  'abandoned',
  'expired',
  'no_show',
];

export const ListingDetailsPage: React.FC = () => {
  const { t } = useTranslation('listing');
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isFetching, error } = useListingDetail(id, { trackView: true });
  const queryClient = useQueryClient();
  const contentRef = useRef<HTMLIonContentElement>(null);
  const shareMutation = useCreateShareLink();
  const patchMutation = usePatchListing();
  const reserveMutation = useReserveOrder();
  const confirmMutation = useConfirmOrder();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  // Optimistic-paint snapshot — only `id` is read here now (the own-listing
  // check below). Falls back to live activeProfile post-whoami.
  const displayProfile = useDisplayProfile();

  const history = useHistory();
  const location = useLocation();
  // Order id a notification deep-linked to (chef path: /orders/{id} redirects
  // here as /listing/{id}?order={id}). Drives the scroll-to + highlight of the
  // matching order card below.
  const targetOrderId = new URLSearchParams(location.search).get('order');
  // `?run=1`: a notification-center drop-off card sent the chef here to start
  // their drop-off run, so land on the run panel rather than an order card.
  const targetDropoffRun = new URLSearchParams(location.search).get('run') === '1';
  const [pickerOpen, setPickerOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [cancelAlert, setCancelAlert] = useState(false);
  const [stopOrdersAlert, setStopOrdersAlert] = useState(false);
  const [portionsChangedAlert, setPortionsChangedAlert] = useState(false);
  const [sharePromptOpen, setSharePromptOpen] = useState(false);
  // Share URL minted on load (see effect below) so the Share tap can call
  // navigator.share() synchronously — Safari requires it inside the gesture.
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [locationGateOpen, setLocationGateOpen] = useState(false);
  // Distinct from `locationGateOpen` (the order address gate): this explains
  // why distance is hidden and offers device geolocation, opened from the
  // dish card's "no location" pin.
  const [distanceGateOpen, setDistanceGateOpen] = useState(false);
  // Order intent that needs to resume after a gate: signing in, or setting a
  // location. The order flow has two preconditions — be signed in, and have a
  // location/locale (needed for tax + to know where the buyer is). A share-link
  // visitor can land here with neither.
  const orderAfterAuthRef = useRef(false);
  const orderAfterLocationRef = useRef(false);

  // The user's own locale (the thing the top-bar pill represents).
  const userLocaleId = useLocationStore((s) => s.localeId);

  const isOwnListing = !!(
    displayProfile?.id &&
    data?.listerSummary?.id &&
    displayProfile.id === data.listerSummary.id
  );

  // Not orderable, by any route: retired (canceled/expired) or taking no more
  // orders (the lister stopped early, or the portions ran out). Sold-out
  // listings stay on the feed, and retired ones are reachable too: a share link
  // outlives its time window, and GET /listings/{id} serves any status to
  // anyone. So the CTA has to cover all of them. Same helper the DishInfoCard
  // above uses, so the card and the CTA can never disagree.
  const { unavailable: isUnavailable } = getListingAvailability(data);

  // Single gated entry point to ordering: require sign-in, then a location,
  // then open the portion picker. Called from the Order button and re-called
  // after each gate clears, so it re-evaluates what's still missing.
  function proceedToOrder() {
    if (!isAuthenticated) {
      orderAfterAuthRef.current = true;
      setSignInOpen(true);
      return;
    }
    // Block ordering until the orderer's PROFILE has a tax-sufficient address
    // (country + postal) — read straight from the store so a resume right after
    // the capture mutation's setActiveProfile sees the fresh value, not a stale
    // render closure. Gating on the profile (not the local location store)
    // closes the hole where a sufficient store value that was never mirrored to
    // the profile let the order slip through to a later tax failure.
    if (!hasOrderableProfileAddress(useProfileStore.getState().activeProfile)) {
      // Don't set the resume ref here — only on a successful location set
      // (onLocationSet). Setting it on open would make a manual Close re-run
      // proceedToOrder and reopen the gate (location still missing).
      setLocationGateOpen(true);
      return;
    }
    if (data && !isUnavailable) setPickerOpen(true);
  }

  // Resume after an in-place sign-in: when auth flips true and the user got
  // here by tapping Order, continue the order flow (which then checks location).
  useEffect(() => {
    if (!orderAfterAuthRef.current || !isAuthenticated) return;
    orderAfterAuthRef.current = false;
    setSignInOpen(false);
    proceedToOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const isDraft = data?.status === 'draft';
  // This listing is past its life: the owner's only move is to duplicate it.
  const isDuplicateOnly = isRelistSource(data);

  useEffect(() => {
    // Wait for any in-flight refetch to settle before deciding to redirect.
    // After publishing a draft, the cached value is briefly still 'draft'
    // until the invalidated query refetches with the new 'scheduled' status.
    if (isFetching) return;
    if (isDraft && isOwnListing && id) {
      history.replace(`/publish-listing/${id}`);
    }
  }, [isDraft, isOwnListing, id, history, isFetching]);

  // Fire confetti and open the share-prompt only after we've confirmed the
  // listing is no longer a draft server-side. The publish flow set a
  // sessionStorage flag keyed by id; we consume it once data lands as
  // not-draft so the celebration matches the actual published state and
  // can never fire twice on the same listing.
  useEffect(() => {
    if (!id || !data || isFetching || isDraft) return;
    if (consumeJustPublished(id)) {
      fireConfetti();
      setSharePromptOpen(true);
    }
  }, [id, data, isFetching, isDraft]);

  /**
   * Pull-to-refresh. Re-pulls the listing itself plus, for the owner, the
   * order list rendered underneath it — those are the two queries this page
   * reads, and both go stale from activity the viewer can't see (someone else
   * taking the last portion, an order advancing).
   *
   * Prefix-invalidating `['listing', id]` rather than calling the query's own
   * `refetch` is deliberate: the key also carries the browse anchor
   * (`['listing', id, anchor]`), so a plain refetch would leave entries for
   * other anchors stale behind it.
   *
   * Stable identity via useCallback because PullToRefresh rebuilds its gesture
   * whenever `onRefresh` changes — an inline arrow would tear down and
   * recreate the gesture on every render of this page.
   */
  const handleRefresh = useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ['listing', id] }),
        queryClient.invalidateQueries({ queryKey: ['listing-orders', id] }),
      ]),
    [queryClient, id],
  );

  // Forced refetch on return from checkout. `useListingDetail` holds a 30s
  // staleTime, which is right for ordinary feed → detail → back churn and
  // wrong for the checkout round-trip: reserving holds a portion, and bailing
  // (or paying, or letting the hold lapse) changes the count again. Landing
  // back here inside that window would otherwise re-render the cached
  // pre-checkout number. The mark is set just before we hand off to checkout
  // and consumed here on the way back — this page genuinely unmounts on
  // navigation (plain react-router <Switch>, no IonRouterOutlet), so the
  // remount is a reliable place to catch it.
  useEffect(() => {
    if (!id) return;
    if (consumeListingStale(id)) {
      void queryClient.invalidateQueries({ queryKey: ['listing', id] });
      void queryClient.invalidateQueries({ queryKey: ['listing-orders', id] });
    }
  }, [id, queryClient]);

  // Prefetch the share ref as soon as the listing loads so `handleShare` runs
  // synchronously inside the tap. Safari/WebKit only opens the native share
  // sheet when navigator.share() is called with transient user activation — any
  // await between the tap and the call (e.g. minting the ref) drops it and the
  // share fails silently (works on Chrome, which keeps activation across the
  // await; not on iOS Safari). Re-mint only when the listing id changes.
  useEffect(() => {
    if (!id || !data?.id) return;
    let cancelled = false;
    setShareUrl(null);
    shareMutation
      .mutateAsync({ listingId: id, route: `/listings/${id}` })
      .then(({ ref }) => {
        if (!cancelled) setShareUrl(`${window.location.origin}/share?ref=${ref}`);
      })
      .catch(() => {
        // Leave shareUrl null; handleShare falls back to minting on tap.
      });
    return () => {
      cancelled = true;
    };
    // shareMutation identity is stable across renders; re-mint on listing change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, data?.id]);

  const obfuscatedCenter = (() => {
    if (!data?.obfuscatedLocation) return null;
    const [lat, lng] = data.obfuscatedLocation.split(',').map(Number);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  })();

  // Resolve the listing's locale from its (obfuscated) center so we can warn a
  // viewer whose own locale differs — e.g. a share-link visitor looking at a
  // dish in another city. Non-owners only get `obfuscatedLocation`, but its
  // center still resolves to the listing's locale (obfuscation < locale size).
  const { data: listingLocale } = useLocale(
    obfuscatedCenter ? { lat: obfuscatedCenter.lat, lon: obfuscatedCenter.lng } : null,
  );
  const listingLocaleLabel = listingLocale?.name || listingLocale?.city || null;
  const localeMismatch =
    !isOwnListing &&
    !!userLocaleId &&
    !!listingLocale &&
    listingLocale.id !== userLocaleId;

  const { data: listingOrders } = useListingOrders(isOwnListing ? id : undefined);
  // "Active" = still in flight; exclude terminal states (already finished,
  // canceled, or never completed). Mirrors the terminal set in OrderStatusBreadcrumb.
  const activeOrders = (listingOrders ?? []).filter(
    (o) => !TERMINAL_ORDER_STATUSES.includes(o.status as OrderStatus),
  );
  // Completed = the settled, ate-the-food outcome. Free-completed rows have
  // grossEarnedCents absent (no payment snapshot); the FE renders "Free" for
  // them. The chef listing's completed-orders section reads these; the sum
  // of grossEarnedCents drives the borderless "Total earned" footer card
  // (only rendered when there IS earnings to show).
  const completedOrders = (listingOrders ?? [])
    .filter((o) => o.status === 'completed')
    .slice()
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const totalEarnedCents = completedOrders.reduce(
    (sum, o) => sum + (o.grossEarnedCents ?? 0),
    0,
  );

  // When a notification deep-links to a specific order that's already terminal
  // (e.g. the chef taps an "order canceled by eater" / "expired" push), it's
  // been filtered out of `activeOrders`. Surface it alongside them — pinned
  // first — so the chef still lands on the card they were notified about.
  const targetOrder = targetOrderId
    ? (listingOrders ?? []).find((o) => o.orderId === targetOrderId)
    : undefined;
  const displayedOrders =
    targetOrder && !activeOrders.some((o) => o.orderId === targetOrderId)
      ? [targetOrder, ...activeOrders]
      : activeOrders;
  // No-shows get their own section: they're terminal, so they drop out of the
  // active list, but the chef can still reopen one for a late arrival, and the
  // completed section is only the ate-the-food outcome. A deep-linked no-show
  // already pinned above isn't repeated here.
  const noShowOrders = (listingOrders ?? []).filter(
    (o) => o.status === 'no_show' && !displayedOrders.some((d) => d.orderId === o.orderId),
  );

  // "Mark all orders ready": count accepted orders waiting to be readied.
  const markAllReadyMutation = useMarkAllOrdersReady();
  // Pickup only: drop-off orders go out together via the drop-off run instead.
  const acceptedPickupCount = activeOrders.filter(
    (o) => o.status === 'accepted' && o.fulfillmentMethod !== 'dropoff',
  ).length;
  const autoReadyOn = !!data?.autoReadyEnabled;
  const handleMarkAllReady = () => {
    if (!id) return;
    markAllReadyMutation.mutate({ listingId: id, enabled: true });
  };
  const handleDisableAutoReady = () => {
    if (!id) return;
    markAllReadyMutation.mutate({ listingId: id, enabled: false });
  };

  const isReserving = reserveMutation.isPending || confirmMutation.isPending;

  const handleShare = async () => {
    if (!id) return;

    try {
      // Prefer the ref minted on load so this stays synchronous inside the tap
      // (required for navigator.share on Safari). Only if the prefetch hasn't
      // landed yet do we mint now — that await-then-share path works on Chrome
      // but not Safari, so it's a last resort, not the normal path.
      const url =
        shareUrl ??
        `${window.location.origin}/share?ref=${
          (
            await shareMutation.mutateAsync({
              listingId: id,
              route: `/listings/${id}`,
            })
          ).ref
        }`;

      if (navigator.share) {
        await navigator.share({ title: data?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (err) {
      // AbortError (user dismissed the sheet) is swallowed; real failures —
      // Safari's NotAllowedError, clipboard rejections — get logged. Network
      // mint errors are additionally handled (modal) by apiService.
      logShareFailure(err, 'listing-share');
    }
  };

  const handlePickerConfirm = async (result: PortionPickerResult) => {
    if (!id) return;

    try {
      const reserved = await reserveMutation.mutateAsync({
        listingId: id,
        quantity: result.quantity,
        fulfillmentMethod: result.fulfillmentMethod,
        saleDate: result.pickupTime,
        // Drop-off carries the destination explicitly. Pickup omits it and the
        // backend falls back to the caller's profile address, as before.
        address: result.address ?? undefined,
      });

      const confirmed = await confirmMutation.mutateAsync({
        id: reserved.orderId,
        body: { intendedPickupTime: result.pickupTime },
      });

      setPickerOpen(false);
      if (confirmed.totalPaid === 0) {
        history.push(`/orders/${reserved.orderId}`);
        return;
      }
      // Whatever happens in checkout — pay, bail, or time out — this listing's
      // portion count moves. Mark it so the remount on the way back refetches
      // past `staleTime` instead of re-rendering the pre-reserve number.
      if (id) markListingStale(id);
      history.push(`/orders/${reserved.orderId}/checkout`, {
        expiresAt: reserved.expiresAt,
        clientSecret: confirmed.clientSecret,
        customerSessionClientSecret: confirmed.customerSessionClientSecret,
        totalPaid: confirmed.totalPaid,
        orderNumber: reserved.orderNumber,
        paymentId: confirmed.paymentId,
        amountSubtotal: confirmed.amountSubtotal,
        amountTax: confirmed.amountTax,
        amountFees: confirmed.amountFees,
        amountTotal: confirmed.amountTotal,
        // Checkout has no listing query of its own, so the pickup the buyer
        // just chose rides along in nav state for it to restate. Without this
        // the payment screen names no day at all.
        pickupTime: result.pickupTime,
        pickupTimezone: data?.timeWindow?.timezone,
        // A drop-off arrives somewhere in the chef's window rather than at the
        // window start carried as pickupTime, so checkout states the window.
        fulfillmentMethod: result.fulfillmentMethod,
        dropoffWindow:
          result.fulfillmentMethod === 'dropoff' ? (data?.dropoffWindow ?? undefined) : undefined,
        dishTitle: data?.title,
        quantity: result.quantity,
        dropoffFee:
          result.fulfillmentMethod === 'dropoff' ? (data?.dropoffFee ?? undefined) : undefined,
        deliveryAddress:
          result.fulfillmentMethod === 'dropoff' ? (result.address ?? undefined) : undefined,
      });
    } catch (err) {
      // /orders/reserve 400s bypass the global error modal so we can surface a
      // friendlier "portions updated" prompt. The reserve mutation has already
      // invalidated the listing query in onSettled, so by the time the user
      // dismisses the alert and reopens the picker, maxQuantity reflects the
      // current count.
      const e = err as Error & {
        status?: number;
        userMessage?: string;
        errorCode?: string;
        correlationId?: string;
      };
      if (e?.status === 400 && e.message?.includes('items remaining')) {
        setPickerOpen(false);
        setPortionsChangedAlert(true);
      } else {
        // Other reserve/confirm failures: surface the generic error modal
        // ourselves since apiService no longer auto-shows it for /orders/reserve.
        // Same message precedence as apiService's global modal: the translated
        // coded error, then the BE's prose, never the raw response body.
        if (e?.status === 400 && e.message) {
          showErrorModal(e.userMessage ?? e.message, e.correlationId, e.errorCode);
        }
        // confirm() failures still go through the global modal in apiService.
      }
    }
  };

  return (
    <IonPage>
      {/* TopBar renders for everyone, authed or not. An anon visitor (typical:
          arriving via a `/share?ref=…` redirect) gets its guest variant: logo,
          location pill, and the hamburger to GuestMenu, whose first card is the
          Sign In CTA. This page used to swap in a Sign-In-only row instead,
          which left a deep-linked visitor with no logo and — at history depth 0
          — no back arrow either, i.e. no way off the page at all. The order CTA
          below still opens the sign-in modal directly at the moment it's
          actually needed. */}
      <TopBar />
      <IonContent ref={contentRef}>
        <PullToRefresh contentRef={contentRef} onRefresh={handleRefresh}>
        <div className="mx-auto w-full max-w-md p-4 space-y-4">
          {isLoading && <p className="text-center text-muted-foreground">{t('common:state.loading')}</p>}
          {error && <p className="text-center text-brand-danger">{t('detail.errorLoad')}</p>}
          {data && isDraft && !isOwnListing && (
            <p className="text-center text-muted-foreground">{t('detail.notFound')}</p>
          )}
          {data && isDraft && isOwnListing && (
            <p className="text-center text-muted-foreground">{t('common:state.loading')}</p>
          )}
          {data && !isDraft && (
            <>
              {isAuthenticated && (
                <div className="flex justify-end gap-1">
                  {isOwnListing && (
                    <button
                      type="button"
                      onClick={() => history.push(`/publish-listing/${id}`)}
                      className="btn-outline inline-flex items-center gap-1.5 rounded-full border-brand-primary px-2 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10"
                    >
                      {/* Past listings (expired/canceled/sold-out, or a window
                          that has closed) can't be edited at all — the BE
                          rejects the status transition out of `expired`. The
                          publish form already handles them by duplicating; this
                          label is what tells the chef that up front, instead of
                          promising an edit that silently becomes a new dish.
                          Both read from isRelistSource so they can't disagree. */}
                      <span>{isDuplicateOnly ? t('detail.duplicateListing') : t('detail.editListing')}</span>
                      <IonIcon icon={isDuplicateOnly ? copyOutline : createOutline} className="text-xl" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleShare}
                    disabled={shareMutation.isPending}
                    className="btn-outline inline-flex items-center gap-1.5 rounded-full border-brand-primary px-2 py-1.5 text-sm font-medium text-brand-primary hover:bg-brand-primary/10 disabled:opacity-50"
                  >
                    <span>{t('detail.shareListing')}</span>
                    <IonIcon icon={shareSocialOutline} className="text-xl" />
                  </button>
                </div>
              )}
              {localeMismatch && (
                <div className="rounded-lg border border-[var(--ion-color-warning-shade)] bg-[var(--ion-color-warning-tint)] p-3 text-sm text-foreground">
                  {listingLocaleLabel
                    ? t('detail.localeMismatch', { locale: listingLocaleLabel })
                    : t('detail.localeMismatchGeneric')}
                </div>
              )}
              <DishInfoCard
                listing={data}
                defaultExpanded
                // The chef needs no "how far is this from me" prompts on their
                // own dish.
                showDistanceGate={!isOwnListing}
                ownListing={isOwnListing}
                onDistanceGateClick={() => setDistanceGateOpen(true)}
                showSoldOutStamp
              />
              {obfuscatedCenter && !isOwnListing && (
                <ListingLocationMap center={obfuscatedCenter} showCircle />
              )}
              {isOwnListing && (
                <ListingLocationMap
                  // The owner gets the real location; centre the radius on it.
                  center={
                    data.listingLocation?.lat != null && data.listingLocation?.lon != null
                      ? { lat: data.listingLocation.lat, lng: data.listingLocation.lon }
                      : (obfuscatedCenter ?? { lat: 0, lng: 0 })
                  }
                  // Drop-off listings preview the area the chef agreed to drive.
                  radiusMiles={
                    data.fulfillmentMethod?.includes('dropoff') ? (data.dropoffRadius ?? null) : null
                  }
                />
              )}

              {/* Lister: incoming orders */}
              {isOwnListing && displayedOrders.length > 0 && (() => {
                return (
                  <div className="space-y-3">
                    <h2 className="text-base font-semibold">
                      {t('detail.ordersHeading', { count: displayedOrders.length })}
                    </h2>
                    {/* Mark all orders ready / auto-ready state. When armed,
                        newly-accepted orders flip to ready automatically
                        server-side (until the pickup window closes or the lister
                        turns it off here). */}
                    {autoReadyOn ? (
                      <div className="flex items-center justify-between gap-3 rounded-xl border border-brand-primary bg-brand-primary/10 px-3 py-2">
                        <span className="text-sm font-medium text-brand-primary">
                          {t('detail.autoReadyOn')}
                        </span>
                        <button
                          type="button"
                          className="btn-outline shrink-0 rounded-lg border-brand-primary bg-card px-3 py-1.5 text-sm font-semibold text-brand-primary active:opacity-80 disabled:opacity-50"
                          disabled={markAllReadyMutation.isPending}
                          onClick={handleDisableAutoReady}
                        >
                          {t('detail.autoReadyTurnOff')}
                        </button>
                      </div>
                    ) : (
                      acceptedPickupCount > 0 && (
                        <button
                          type="button"
                          className="btn-outline w-full rounded-xl border-brand-primary py-3 text-base font-semibold text-brand-primary active:opacity-80 disabled:opacity-50"
                          disabled={markAllReadyMutation.isPending}
                          onClick={handleMarkAllReady}
                        >
                          {t('detail.markAllReadyCta', { count: acceptedPickupCount })}
                        </button>
                      )
                    )}
                    {id && <DropoffRunPanel
                        listingId={id}
                        orders={listingOrders ?? []}
                        runStartedAt={data.dropoffRunStartedAt}
                        highlight={targetDropoffRun}
                        dropoffWindow={data.dropoffWindow}
                      />}
                    {displayedOrders.map((order) => (
                      <OrderCard
                        key={order.orderId}
                        order={order}
                        isLister
                        pickupTimezone={data?.timeWindow?.timezone}
                        anchorId={`order-${order.orderId}`}
                        highlight={order.orderId === targetOrderId}
                      />
                    ))}
                  </div>
                );
              })()}

              {isOwnListing && noShowOrders.length > 0 && (
                <div className="space-y-3">
                  <h2 className="text-base font-semibold">
                    {t('detail.noShowOrdersHeading', { count: noShowOrders.length })}
                  </h2>
                  {noShowOrders.map((order) => (
                    <OrderCard
                      key={order.orderId}
                      order={order}
                      isLister
                      pickupTimezone={data?.timeWindow?.timezone}
                      anchorId={`order-${order.orderId}`}
                    />
                  ))}
                </div>
              )}

              {/* Lister actions. Stop taking orders (outline) sets status to
                  sold_out — new orders blocked, drops from feed, existing orders
                  keep going; only valid while still orderable (scheduled/active).
                  Status alone isn't enough: a listing whose portions ran out, or
                  whose window closed before the expiry worker ticked, still
                  reads active, so also gate on availability and window end.
                  Cancel (danger) voids the listing and its orders. */}
              {isOwnListing &&
                data.status &&
                ['draft', 'scheduled', 'active'].includes(data.status) && (
                  <div className="flex flex-col gap-2">
                    {['scheduled', 'active'].includes(data.status) &&
                      !isUnavailable &&
                      !isDuplicateOnly && (
                      <button
                        type="button"
                        className="btn-outline flex-1 rounded-xl border-brand-danger py-3 text-base font-semibold text-brand-danger active:opacity-80 disabled:opacity-50"
                        disabled={patchMutation.isPending}
                        onClick={() => setStopOrdersAlert(true)}
                      >
                        {t('detail.stopOrdersCta')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="flex-1 rounded-xl bg-brand-danger py-3 text-base font-semibold text-white active:opacity-80 disabled:opacity-50"
                      disabled={patchMutation.isPending}
                      onClick={() => setCancelAlert(true)}
                    >
                      {patchMutation.isPending ? t('detail.cancelling') : t('detail.cancelListingCta')}
                    </button>
                  </div>
                )}

              {/* Lister: completed orders. Sits AFTER the destructive lister
                  actions above — the settled history is reference data, not
                  something the chef acts on from here, so putting it last
                  keeps the actionable Stop/Cancel buttons in easy reach and
                  lets the settled orders scroll off-screen naturally. Sum of
                  grossEarnedCents drives the borderless "Total earned" footer
                  that deep-links into the earnings page. Free-completed orders
                  read grossEarnedCents=undefined and render Total="Free". */}
              {isOwnListing && (
                <div className="space-y-3 border-t border-border pt-6">
                  <h2 className="text-base font-semibold">
                    {t('detail.completedOrdersHeading')}
                  </h2>
                  {completedOrders.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      {t('detail.completedOrdersEmpty')}
                    </p>
                  ) : (
                    <>
                      {completedOrders.map((order) => (
                        <OrderDetailCard
                          key={order.orderId}
                          orderNumber={order.orderNumber}
                          quantity={order.quantity}
                          fulfillmentMethod={order.fulfillmentMethod}
                          pickupTime={order.intendedPickupTime}
                          dropoffWindow={order.dropoffWindow}
                          orderedAt={order.createdAt}
                          timezone={data.timeWindow?.timezone}
                          total={
                            order.grossEarnedCents && order.grossEarnedCents > 0
                              ? formatPriceCents(order.grossEarnedCents)
                              : t('detail.completedOrdersFree')
                          }
                          userAttribution={
                            order.orderedFor
                              ? {
                                  id: order.orderedFor.id,
                                  displayName: order.orderedFor.displayName,
                                  businessName: order.orderedFor.businessName,
                                  avatarUrl: order.orderedFor.avatarUrl,
                                  connectionLevel: order.orderedFor.connectionLevel,
                                }
                              : undefined
                          }
                        />
                      ))}
                      {totalEarnedCents > 0 && (
                        <button
                          type="button"
                          onClick={() => history.push('/my-earnings')}
                          className="flex w-full items-center justify-between rounded-xl p-4 text-base font-semibold text-brand-primary hover:bg-muted/30 active:opacity-80 transition"
                        >
                          <span>{t('detail.totalEarnedLabel')}</span>
                          <span>{formatPriceCents(totalEarnedCents)}</span>
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        </PullToRefresh>
      </IonContent>
      {/* Order CTA. The whole footer goes away once the listing can't be
          ordered rather than leaving a dead disabled button: the DishInfoCard
          above always names the reason in that state (Orders closed / Sold out
          / Unavailable), so nothing is lost by dropping the second, vaguer
          copy of it down here. */}
      {data && !isOwnListing && !isDraft && !isUnavailable && (
        <IonFooter className="ion-no-border">
          <IonToolbar>
            <div className="mx-auto w-full max-w-md px-4 py-2">
              <button
                type="button"
                className="w-full rounded-xl bg-brand-primary py-3 text-base font-semibold text-white active:opacity-80"
                onClick={proceedToOrder}
              >
                {data.price != null
                  ? t('detail.orderCtaWithPrice', { price: formatPriceCents(data.price) })
                  : t('detail.orderCta')}
              </button>
            </div>
          </IonToolbar>
        </IonFooter>
      )}
      {data && (
        <PortionPickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onConfirm={handlePickerConfirm}
          listing={data}
          isSubmitting={isReserving}
        />
      )}
      <SignInModal
        isOpen={signInOpen}
        onClose={() => setSignInOpen(false)}
        intro={t('detail.signInIntro')}
      />
      <AddressCaptureModal
        isOpen={locationGateOpen}
        // One-tap option to adopt the dish's location (its obfuscated center,
        // reverse-geocoded) alongside the usual current-location / ZIP options.
        dishCoords={obfuscatedCenter ? { lat: obfuscatedCenter.lat, lon: obfuscatedCenter.lng } : null}
        // The dish's locale labels the button and gates the ZIP path (a ZIP in a
        // different locale prompts a confirmation before committing).
        dishLocaleId={listingLocale?.id ?? null}
        dishLocaleName={listingLocaleLabel}
        // Success: mark resume + close; the picker opens once the modal is fully
        // dismissed (onClose via onDidDismiss) to avoid overlapping modals.
        onLocationSet={() => {
          orderAfterLocationRef.current = true;
          setLocationGateOpen(false);
        }}
        onClose={() => {
          setLocationGateOpen(false);
          if (orderAfterLocationRef.current) {
            orderAfterLocationRef.current = false;
            proceedToOrder();
          }
        }}
      />
      <PublishedSharePromptModal
        isOpen={sharePromptOpen}
        onClose={() => setSharePromptOpen(false)}
        onShare={() => {
          handleShare();
        }}
        isPending={shareMutation.isPending}
      />
      <IonAlert
        isOpen={portionsChangedAlert}
        onDidDismiss={() => setPortionsChangedAlert(false)}
        header={t('detail.portionsChangedTitle')}
        message={
          data?.itemsRemaining && data.itemsRemaining > 0
            ? t('detail.portionsChangedSomeLeft', { count: data.itemsRemaining })
            : t('detail.portionsChangedSoldOut')
        }
        buttons={[{ text: t('detail.alertOk'), role: 'cancel' }]}
      />
      <CancelListingModal
        isOpen={cancelAlert}
        onClose={() => setCancelAlert(false)}
        onConfirm={(reason) => {
          if (id) {
            patchMutation.mutate(
              { id, fields: { status: 'canceled', cancellationReason: reason } },
              { onSuccess: () => setCancelAlert(false) },
            );
          }
        }}
        isPending={patchMutation.isPending}
        requireReason
        title={t('detail.cancelConfirmTitle')}
        description={
          activeOrders.length > 0
            ? t('detail.cancelConfirmMessageWithOrders', { count: activeOrders.length })
            : t('detail.cancelConfirmMessage')
        }
        confirmLabel={
          activeOrders.length > 0
            ? t('detail.cancelConfirmConfirmWithOrders')
            : t('detail.cancelConfirmConfirm')
        }
        cancelLabel={t('detail.cancelConfirmKeep')}
      />
      <CancelListingModal
        isOpen={stopOrdersAlert}
        onClose={() => setStopOrdersAlert(false)}
        onConfirm={() => {
          if (id) {
            patchMutation.mutate(
              { id, fields: { status: 'sold_out' } },
              { onSuccess: () => setStopOrdersAlert(false) },
            );
          }
        }}
        isPending={patchMutation.isPending}
        title={t('detail.stopOrdersConfirmTitle')}
        description={t('detail.stopOrdersConfirmMessage')}
        confirmLabel={t('detail.stopOrdersConfirmConfirm')}
        cancelLabel={t('detail.stopOrdersConfirmKeep')}
      />
      <DistanceLocationGateModal
        isOpen={distanceGateOpen}
        onClose={() => setDistanceGateOpen(false)}
      />
    </IonPage>
  );
};
